const EM_DASH = '—';

function withCommas(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function compactSuffix(n: number, suffix: string): string {
  const fixed = n.toFixed(1);
  const rounded = parseFloat(fixed);
  const display = rounded % 1 === 0 ? String(Math.round(rounded)) : fixed;
  return display + suffix;
}

export function formatCount(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return EM_DASH;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return EM_DASH;
  if (num === 0) return '0';
  const abs = Math.abs(num);
  if (abs >= 1_000_000_000) return compactSuffix(num / 1_000_000_000, 'B');
  if (abs >= 1_000_000) return compactSuffix(num / 1_000_000, 'M');
  if (abs >= 1_000) return compactSuffix(num / 1_000, 'K');
  return String(Math.round(num));
}

export function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return EM_DASH;
  const pct = value * 100;
  if (pct === 0) return '0%';
  if (Math.abs(pct) < 0.05) return '<0.1%';
  if (Math.abs(pct) < 10) return parseFloat(pct.toFixed(1)) + '%';
  return Math.round(pct) + '%';
}

export function formatPercent(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined) return EM_DASH;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return EM_DASH;
  if (num === 0) return '0%';
  if (Math.abs(num) < 10) return parseFloat(num.toFixed(1)) + '%';
  return Math.round(num) + '%';
}

export function formatScore(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return EM_DASH;
  return withCommas(Math.round(value));
}

export function formatReturn(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return EM_DASH;
  if (value === 0) return '0x';
  if (Math.abs(value) < 10) return parseFloat(value.toFixed(1)) + 'x';
  return Math.trunc(value) + 'x';
}

/**
 * Percentage change of `curr` vs `prev`, rounded to a whole number, with a
 * sign-prefixed label and a positive/negative flag for coloring. Returns null
 * when a meaningful delta can't be computed (null/undefined point or zero
 * baseline) so callers can omit the badge instead of rendering "NaN%"/"Infinity%".
 */
export function computePctChange(
  curr: number | null | undefined,
  prev: number | null | undefined,
): { text: string; positive: boolean } | null {
  if (curr === null || curr === undefined || !Number.isFinite(curr))
    return null;
  if (
    prev === null ||
    prev === undefined ||
    !Number.isFinite(prev) ||
    prev === 0
  )
    return null;
  const rounded = Math.round(((curr - prev) / prev) * 100);
  const positive = rounded >= 0;
  return { text: `${positive ? '+' : ''}${rounded}%`, positive };
}

export function formatMoney(
  value: number | string | null | undefined,
  opts?: { cents?: boolean },
): string {
  if (value === null || value === undefined) return EM_DASH;
  const value_ = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(value_)) return EM_DASH;

  const negative = value_ < 0;
  const sign = negative ? '-' : '';
  const abs = Math.abs(value_);

  // Truncate to whole cents — money must never be rounded up. The +1e-6 absorbs
  // floating-point error (e.g. 12.345 * 100 = 1234.4999…) without rounding up.
  const totalCents = Math.floor(abs * 100 + 1e-6);
  const intPart = Math.floor(totalCents / 100);
  const fracPart = totalCents % 100;

  if (opts?.cents) {
    return (
      '€' +
      sign +
      withCommas(intPart) +
      '.' +
      fracPart.toString().padStart(2, '0')
    );
  }

  if (abs >= 1_000_000_000)
    return '€' + compactSuffix(value_ / 1_000_000_000, 'B');
  if (abs >= 1_000_000) return '€' + compactSuffix(value_ / 1_000_000, 'M');
  if (fracPart === 0) return '€' + sign + withCommas(intPart);
  return (
    '€' +
    sign +
    withCommas(intPart) +
    '.' +
    fracPart.toString().padStart(2, '0')
  );
}

export const formatCompactNumber = (value: number, decimals = 1): string => {
  const abs = Math.abs(value);

  if (abs < 1000) {
    return Math.round(value).toString();
  } else if (abs < 1000000) {
    const formatted = (value / 1000).toFixed(decimals);
    return Number(formatted) % 1 === 0
      ? formatted.replace('.0', '') + 'K'
      : formatted + 'K';
  } else if (abs < 1000000000) {
    const formatted = (value / 1000000).toFixed(decimals);
    return Number(formatted) % 1 === 0
      ? formatted.replace('.0', '') + 'M'
      : formatted + 'M';
  } else {
    const formatted = (value / 1000000000).toFixed(decimals);
    return Number(formatted) % 1 === 0
      ? formatted.replace('.0', '') + 'B'
      : formatted + 'B';
  }
};
